import type { ICitaOrdenesTecnologia } from '@/modules/Admision/interfaces/tablas/ICitaOrdenesTecnologia'
import type { ICitaOrdenesTecnologiaDetalle } from '@/modules/Admision/interfaces/tablas/ICitaOrdenesTecnologia'
import { pdfTableLayout } from './pdfStyles'
import type { ICitaDiagnostico, ICitaIncapacidadesCertificados, ICitaOrdenes, ICitaTechnologiasSalud } from '@/modules/Asistencial/interfaces';
import { isValidHeader, tipoImpresionAsistencialDefault } from '../constantes';
import type { IConfMaestra } from '@/modules/Configuracion/interfaces';
import { getDiagnosticDataSimple } from './diagnosticData';
import type { IConfPersonasEmpresa } from '@/modules/Administracion/interfaces';
import { getDocumentHeader } from './pdfHeader';

const ADMISIONES_EXTERNAS = ['ORDEN EXTERNA', 'ENTREGA EXTERNA']

export function getServicioOrdenLineas(orden?: ICitaOrdenesTecnologia | null): string[] {
  if (!orden) return []
  const servicioContrato = orden.contratoxservicio?.servicio?.coseNombre || ''
  const servicioExterno = orden.ciotServOrden || ''
  const servicio = ADMISIONES_EXTERNAS.includes(orden.ciotTipoAdmision || '')
    ? servicioExterno || servicioContrato
    : servicioContrato || servicioExterno
  const cup = orden.contratoxservicio?.cup
  const cups = [cup?.cocuCups, cup?.cocuNombre].filter(Boolean).join(' - ')
  return [
    ...(servicio ? [`Servicio: ${servicio}`] : []),
    ...(cups ? [`CUPS: ${cups}`] : [])
  ]
}

export interface ICitaOrdenesAgrupadas {
  tipoOrden: string
  detalles: ICitaOrdenes[]
}
/**
 * Genera la sección de órdenes médicas para el PDF usando estilos centralizados.
 * @param ordenes Las órdenes médicas a procesar con sus detalles (ICitaOrdenes[])
 * @param options Opciones para personalizar la sección (puede incluir visible: boolean)
 * @returns Un array de tablas (una por tipo de orden) o null si options.visible === false
 */
export function getOrdersData(ordenes: ICitaOrdenesAgrupadas, options?: { maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[],visible?: boolean, firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean }) {
  const { datosPaciente, visible, orderWithCabezera, firma, diagnosticos } = options || {}
  if (visible === false) return null
  if (!ordenes) {
    return [{
      text: 'Órdenes médicas: ',
      style: 'tableHeader',
      margin: [0, 0, 0, 10]
    }]
  }
  const content = [] as unknown[]
    if(orderWithCabezera && isValidHeader(datosPaciente as Array<unknown>)){
      content.push({  layout: pdfTableLayout,table: { widths: ["*"],body: [[{ text: ordenes.tipoOrden as string || 'ÓRDENES', style: 'cardTitle' }]]}})
      content.push(JSON.parse(JSON.stringify(datosPaciente)))
      const diagnosticosData = diagnosticos || []

      if (diagnosticosData && diagnosticosData.length > 0) {
        content.push(getDiagnosticDataSimple(diagnosticosData))
      }
    }
  const body = !orderWithCabezera ?
    [[
      { text: ordenes.tipoOrden, style: 'tableHeader', colSpan: 4 },
      {}, {}, {}
    ]] as unknown[] : []
   body.push(...[[
      { text: 'CUPS', style: 'tableHeader'},
      { text: 'DESCRIPCIÓN', style: 'tableHeader' },
      { text: 'CANTIDAD', style: 'tableHeader' },
      { text: 'OBSERVACIÓN', style: 'tableHeader' }
    ]])
  body.push(...ordenes.detalles.map((detalles) =>
    [
      { text: detalles.cup?.cocuCups || '', style: 'tableCell' },
      { text: detalles.cup?.cocuNombre || '', style: 'tableCell' },
      { text: detalles.ciorCantidad?.toString() || '', style: 'tableCell' },
      { text: detalles.ciorObservacion || '', style: 'tableCell' }
    ]
  ))
  content.push({
    margin: [0, 5, 0, 0],
    stack: [
      {
        margin: [0, 5, 0, 0],
        table: {
          headerRows: 1,
          widths: ['auto', '*', 'auto', '*'],
          body
        },
        layout: pdfTableLayout
      }
    ]
  })
  if(firma && orderWithCabezera) content.push(JSON.parse(JSON.stringify(firma)))
  return content
}

export function getOrdersTecnologiaData(ordenes: ICitaOrdenesTecnologia[], options?: { maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], visible?: boolean, firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean, ips: IConfPersonasEmpresa, headerImage: string }) {
  const { diagnosticos, visible, firma, datosPaciente, orderWithCabezera, ips, headerImage } = options || {}
  if (visible === false) return null
  if (!ordenes || ordenes.length === 0) {
    return [{
      text: 'Órdenes médicas: ',
      style: 'tableHeader',
      margin: [0, 0, 0, 10]
    }]
  }

  const contentOrdenes = ordenes.map((orden) => {
    const detalles: ICitaOrdenesTecnologiaDetalle[] = orden.detalles || [];
    if (!detalles.length) {
      return {
        text: `Órdenes (${orden.ciotTipo || 'Tipo desconocido'}): `,
        style: 'tableHeader',
        margin: [0, 0, 0, 10]
      }
    }
    const content = [] as unknown[]
    if(orderWithCabezera && isValidHeader(datosPaciente as Array<unknown>)){
      content.push(getDocumentHeader(ips as IConfPersonasEmpresa, headerImage))
      content.push({
        layout: 'noBorders',
        table: {
          widths: ["*"],
          body: [[
            { text: ['ORDENES DE ' + orden.ciotTipo as string, 'Ref.: ORD-' + (orden.ciotId || ''), ...getServicioOrdenLineas(orden)].join('\n'), style: 'tableHeaderLeft'}
          ]]
          }
        })
      content.push(JSON.parse(JSON.stringify(datosPaciente)))
      const diagnosticosData = diagnosticos || []

      if (diagnosticosData && diagnosticosData.length > 0) {
        const dataDiagnosticos = JSON.parse(JSON.stringify(getDiagnosticDataSimple(diagnosticosData)))
        content.push(dataDiagnosticos)
      }
    }
    const isRecetario = 'RECETARIOS' == orden.ciotTipo as string
    const body = !orderWithCabezera ?[[
      { text: 'ORDENES DE ' + orden.ciotTipo as string, style: 'tableHeader', colSpan: (isRecetario ? 1 : 4 )},
      ...(isRecetario? []:[{}, {}, {}])
    ]] as unknown[] :[]
    if (isRecetario){
      body.push(...[
      [
        { text: 'MEDICAMENTO', style: 'tableHeader'}
      ]])
      body.push(...detalles.map(detalle => [
        { text: `${detalle.medicamento?.megeNombre || ''} - CONCENTRACIÓN: ${detalle.medicamento?.megeConcentracion || ''} - UNID: ${detalle.medicamento?.unidad?.comaNombrelargo === 'NO APLICA' ? '' : detalle.medicamento?.unidad?.comaNombrelargo || ''}  - FORMA: ${detalle.medicamento?.formafarmaceutica?.comaNombrecorto === 'NO APLICA' ? '' : detalle.medicamento?.formafarmaceutica?.comaNombrecorto || ''} - VÍA: ${detalle.medicamento?.via?.comaNombrelargo === 'NO APLICA' ? '' : detalle.medicamento?.via?.comaNombrelargo || ''}, ${detalle.ciodCantidaddosis?.toString() || ''} cada ${detalle.ciodFrecuenciahoras?.toString() || ''} horas, durante ${detalle.ciodDuraciondias?.toString() || ''} días, la cantidad de ${detalle.ciodCantidadprescrita?.toString() || ''}, vía ${detalle.medicamento?.via?.comaNombrecorto || ''}${detalle.ciodObservacion ? `\nPrescripción: ${detalle.ciodObservacion}` : ''}`, style: 'tableCell' },
      ]))
    } else {
      body.push(...[
      [
        { text: 'CUPS', style: 'tableHeader'},
        { text: 'DESCRIPCIÓN', style: 'tableHeader'},
        { text: 'CANTIDAD', style: 'tableHeader'},
        { text: 'OBSERVACIÓN', style: 'tableHeader'}
      ]])
      body.push(...detalles.map(detalle => [
        { text: detalle.cups?.cocuCups || '', style: 'tableCell' },
        { text: detalle.cups?.cocuNombre || '', style: 'tableCell' },
        { text: detalle.ciodCantidad?.toString() || '', style: 'tableCell'},
        { text: detalle.ciodObservacion || '', style: 'tableCell' }
      ]))
    }


    content.push({
      margin: [0, 5, 0, 0],
      stack: [
        {
          margin: [0, 5, 0, 0],
          table: {
            headerRows: 1,
            widths: isRecetario ? ['*']:['auto', '*', 'auto', '*'],
            body
          },
          layout: pdfTableLayout
        }
      ]
    })
    if(firma && orderWithCabezera) content.push(JSON.parse(JSON.stringify(firma)))
    return content
  })
  return contentOrdenes
}

export function getCertificadosData(items: ICitaIncapacidadesCertificados[], tipo:string, options?:{ technologia?: ICitaTechnologiasSalud, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[],visible?:boolean, firma: unknown[], datosPaciente: unknown[], orderWithCabezera?: boolean, ips: IConfPersonasEmpresa, headerImage: string } ){
  const { technologia, datosPaciente, visible, orderWithCabezera, firma, diagnosticos, ips, headerImage } = options || {}
  if (visible === false) return null
  const contentAll = [] as unknown[]
  if (!items || (Array.isArray(items) && items.length === 0)) {
    return [{
      text: 'Certificados: ',
      style: 'tableHeader',
      margin: [0, 0, 0, 10]
    }]
  }
  const cabecera = items[0].ciceTipo
  const isCertificado = cabecera === 'CERTIFICADO'
  if (tipo !== tipoImpresionAsistencialDefault.HISTORIA || (orderWithCabezera && tipo === tipoImpresionAsistencialDefault.HISTORIA) ) {
    contentAll.push(items.map(item => {
      const content = [] as unknown[]
      const body = [] as unknown[]
      if(orderWithCabezera && isValidHeader(datosPaciente as Array<unknown>)){
        content.push(getDocumentHeader(ips as IConfPersonasEmpresa, headerImage))
        content.push({
          layout: 'noBorders',
          table: {
            widths: ["*"],
            body: [[
              { text: cabecera + (isCertificado ? (' DE ' +(technologia?.contratoxservicio?.servicio?.coseNombre as string)): '') +'\n' + 'Ref.: ' + (isCertificado ? 'CERT-':'INCAP-') +  (item.ciceId|| ''), style: 'tableHeaderLeft'}
            ]]
          }
        })
        content.push(JSON.parse(JSON.stringify(datosPaciente)))
        const diagnosticosData = diagnosticos || []

        if (diagnosticosData && diagnosticosData.length > 0 && !isCertificado) {
          content.push(getDiagnosticDataSimple(diagnosticosData))
        }
      }
      body.push(isCertificado? [{ text: 'CERTIFICADO', style: 'tituloCell' }]:[
        { text: 'DIAS', style: 'tituloCell' },
        { text: 'FECHA DE INICIO', style: 'tituloCell' },
        { text: 'FECHA DE FINALIZACIÓN', style: 'tituloCell' }
      ])
      body.push(isCertificado? [{ text: item.ciceCertificado || '', style: 'tableCell' }]:[
        { text: item.ciceDias?.toString() || '', style: 'tableCell' },
        { text: item.ciceFechainicio || '', style: 'tableCell' },
        { text: item.ciceFechafinal || '', style: 'tableCell' }
      ])

      if (!isCertificado && item.ciceCertificado) {
        body.push([
          { text: 'Observación', style: 'tituloCell', colSpan: 3 },
          {}, {}
        ])
        body.push([
          { text: item.ciceCertificado || '', style: 'tableCell', colSpan: 3 },
          {}, {}
        ])
      }
      content.push({
        margin: [0, 5, 0, 0],
        stack: [
          {
            margin: [0, 5, 0, 0],
            table: {
              headerRows: 1,
              widths: isCertificado ? ['*'] : ['*', '*', '*'],
              body
            },
            layout: pdfTableLayout
          }
        ]
      })
      if(firma && orderWithCabezera) content.push(JSON.parse(JSON.stringify(firma)))
      return content
    }))
  } else {
    const body = [[{ text: cabecera, style: 'tableHeader', colSpan: isCertificado ? 1 : 3 }, ...isCertificado ? [] : [{},{}]] as unknown[]]
    const dataOrden = isCertificado ? items.map(certificado =>
      ([
        { text: certificado.ciceCertificado || '', style: 'tableCell' }
      ])
    ): items.map(incapacidad =>
    ([
      { text: incapacidad.ciceDias?.toString() || '', style: 'tableCell' },
      { text: incapacidad.ciceFechainicio || '', style: 'tableCell' },
      { text: incapacidad.ciceFechafinal || '', style: 'tableCell' }
    ]))
   body.push(isCertificado? [{ text: 'CERTIFICADO', style: 'tituloCell' }]:[
      { text: 'DIAS', style: 'tituloCell' },
      { text: 'FECHA DE INICIO', style: 'tituloCell' },
      { text: 'FECHA DE FINALIZACIÓN', style: 'tituloCell' }
    ])
   body.push(...dataOrden)
    contentAll.push({
      margin: [0, 5, 0, 0],
      stack: [
        {
          margin: [0, 5, 0, 0],
          table: {
            headerRows: 1,
            widths: isCertificado ? ['*'] : ['*', '*', '*'],
            body
          },
          layout: pdfTableLayout
        }
      ]
    })
  }
  return contentAll;
}

export function agruparPorTipoOrden(ordenes: ICitaOrdenes[]): ICitaOrdenesAgrupadas[] {
  const agrupadas: Record<string, {
    tipoOrden: string
    detalles: ICitaOrdenes[]
  }> = {}
  for (const item of ordenes) {
    if (!item.orden || !item.orden.comaNombrelargo) continue
    const key = item.orden.comaNombrelargo
    if (!agrupadas[key]) {
      agrupadas[key] = {
        tipoOrden: key,
        detalles: []
      }
    }
    // Buscar si ya existe el CUPS dentro del grupo
    agrupadas[key].detalles.push({ ...item })
  }
  return Object.values(agrupadas)
}
